import React from "react";
import styled from "styled-components";
import { Typography } from "../ui-kit/typography/Typography";
import { TypographyVariant } from "../ui-kit/typography/Typography.interface";
import { useNavigate } from "react-router-dom";
import somethingWentWrongImage from "./images/something-went-wrong.png";

export interface CriticalErrorPageProps {
  error?: Error;
  errorInfo?: any;
  clearError?: () => void;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background: white;
`;

const ErrorCard = styled.div`
  background: white;
  max-width: 600px;
  width: 100%;
  text-align: center;
  position: relative;
`;

const ImageWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: -130px 0 -30px;

  img {
    max-height: 300px;
    object-fit: contain;
  }
`;

const Title = styled(Typography)`
  margin-bottom: 20px;
  font-weight: bold;
`;

const Subtitle = styled(Typography)`
  margin-bottom: 30px;
  line-height: 1.6;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 20px;
  justify-content: center;
  margin-top: 40px;
  flex-wrap: wrap;
`;

const StyledButton = styled.button`
  min-width: 140px;
  padding: 12px 24px;
  border-radius: 8px;
  font-family: Montserrat;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: #ffffff;
  border: 1px solid #dbe3eb;
  color: #13273f;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  }
`;

const ErrorCode = styled.div`
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 20px;
  margin-top: 30px;
  text-align: left;
  font-family: "Courier New", monospace;
  font-size: 12px;
  color: #dc3545;
  max-height: 200px;
  overflow-y: auto;
`;

export const CriticalErrorPage: React.FC<CriticalErrorPageProps> = ({
  error,
  errorInfo,
  clearError,
}) => {
  const navigate = useNavigate();

  const handleGoBack = () => {
    navigate(-1);
    clearError?.();
  };

  const handleReload = () => {
    window.location.reload();
    clearError?.();
  };

  return (
    <Container>
      <ErrorCard>
        <ImageWrapper>
          <img src={somethingWentWrongImage} alt="Something went wrong" />
        </ImageWrapper>
        <Title variant={TypographyVariant.HEADER1}>
          It seems something went wrong
        </Title>
        <Subtitle variant={TypographyVariant.BODY1}>
          Please try refreshing the page or come back later.
          <br />
          If the problem persists, please contact support at{" "}
          <a href="mailto:support@fena.co">support@fena.co</a>
        </Subtitle>

        <ButtonGroup>
          <StyledButton onClick={handleReload}>Reload Page</StyledButton>
          <StyledButton onClick={handleGoBack}>Go back</StyledButton>
        </ButtonGroup>

        {error &&
          (process.env.REACT_APP_ENV === "dev" ||
            window?.location?.hostname?.includes("localhost")) && (
            <ErrorCode>
              <strong>Error Details:</strong>
              <br />
              {error.message}
              <br />
              <br />
              <strong>Stack Trace:</strong>
              <br />
              {error.stack}
              {errorInfo?.componentStack && (
                <>
                  <br />
                  <br />
                  <strong>Component Stack:</strong>
                  <br />
                  {errorInfo.componentStack}
                </>
              )}
            </ErrorCode>
          )}
      </ErrorCard>
    </Container>
  );
};
